import { ref, computed } from "vue";
import { PublicClientApplication, type AuthenticationResult } from "@azure/msal-browser";

const clientId = import.meta.env.VITE_U3T_ADAL_CLIENT_ID as string;

const GRAPH_SCOPES = [
    "User.Read",
    "Sites.Read.All",
    "Sites.ReadWrite.All",
    "AllSites.Read",
    "openid",
    "profile",
    "email",
];

const SCOPES = ["https://sharepointadapterkpi.azurewebsites.net/user_impersonation"];

const msalConfig = {
    auth: {
        clientId: clientId,
        authority: "https://login.microsoftonline.com/372ee9e0-9ce0-4033-a64a-c07073a91ecd",
        redirectUri: window.location.origin + "/login",
    },
    cache: {
        cacheLocation: "sessionStorage" as const,
    },
    system: {
        tokenRenewalOffsetSeconds: 600,
        iframeHashTimeout: 30000,
    },
};

const token = ref<string>("");
const graphToken = ref<string>("");
const userName = ref<string>("");
const userId = ref<string>("");
const userMail = ref<string>("");
const isAuthenticated = ref<boolean>(false);
const isInitialized = ref<boolean>(false);

let msalInstance: PublicClientApplication | null = null;

export function useAuth() {
    /**
     * Initialize MSAL and handle redirect response
     */
    const initialize = async (): Promise<void> => {
        if (isInitialized.value) return;

        console.log("Initializing authentication...");

        try {
            msalInstance = new PublicClientApplication(msalConfig);
            await msalInstance.initialize();

            const response = await msalInstance.handleRedirectPromise();

            if (response) {
                try {
                    const graphResponse = await msalInstance.acquireTokenSilent({
                        scopes: GRAPH_SCOPES,
                        account: response.account,
                    });
                    setAuthData(response, graphResponse);
                } catch {
                    setAuthData(response);
                }
            } else {
                await tryAcquireTokenSilent();
            }

            isInitialized.value = true;
        } catch (error) {
            console.error("Failed to initialize authentication:", error);
            isInitialized.value = true;
        }
    };

    /**
     * Try to acquire token silently (if user already logged in)
     */
    const tryAcquireTokenSilent = async (): Promise<boolean> => {
        if (!msalInstance) return false;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return false;

        try {
            const apiResponse = await msalInstance.acquireTokenSilent({
                scopes: SCOPES,
                account: accounts[0],
            });

            const graphResponse = await msalInstance.acquireTokenSilent({
                scopes: GRAPH_SCOPES,
                account: accounts[0],
            });

            setAuthData(apiResponse, graphResponse);
            return true;
        } catch (error) {
            console.log("Silent token acquisition failed:", error);
            return false;
        }
    };

    /**
     * Start the login flow - redirects to Azure AD
     */
    const login = async (returnUrl?: string): Promise<void> => {
        if (!msalInstance) {
            await initialize();
        }

        if (returnUrl) {
            sessionStorage.setItem("auth_return_url", returnUrl);
        }

        try {
            await msalInstance!.loginRedirect({
                scopes: SCOPES,
            });
        } catch (error) {
            console.error("Login failed:", error);
            throw error;
        }
    };

    /**
     * Get the saved return URL and clear it from storage
     */
    const getReturnUrl = (): string => {
        const returnUrl = sessionStorage.getItem("auth_return_url") || "/";
        sessionStorage.removeItem("auth_return_url");
        return returnUrl;
    };

    /**
     * Logout the user
     */
    const logout = async (): Promise<void> => {
        if (!msalInstance) return;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            await msalInstance.logoutRedirect({
                account: accounts[0],
            });
        }

        clearAuthData();
    };

    /**
     * Set authentication data from MSAL response
     */
    const setAuthData = (apiResponse: AuthenticationResult, graphResponse?: AuthenticationResult): void => {
        token.value = apiResponse.accessToken;
        graphToken.value = graphResponse?.accessToken ?? "";
        userName.value = apiResponse.account?.name ?? "";
        userId.value = apiResponse.account?.localAccountId ?? "";
        userMail.value = apiResponse.account?.username ?? "";
        isAuthenticated.value = true;
    };

    /**
     * Clear authentication data
     */
    const clearAuthData = (): void => {
        token.value = "";
        graphToken.value = "";
        userName.value = "";
        userId.value = "";
        userMail.value = "";
        isAuthenticated.value = false;
    };

    return {
        token: computed(() => token.value),
        graphToken: computed(() => graphToken.value),
        userName: computed(() => userName.value),
        userId: computed(() => userId.value),
        userMail: computed(() => userMail.value),
        isAuthenticated: computed(() => isAuthenticated.value),
        isInitialized: computed(() => isInitialized.value),

        initialize,
        login,
        logout,
        getReturnUrl,
    };
}
